import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useState } from 'react';
import { useClientExportFlow } from '../../hooks/useClientExportFlow';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import BandwidthPanelExportShell from './BandwidthPanelExportShell';
import type { BandwidthCrossFilterSlice } from './bandwidthCrossFilter';
import type { BandwidthCapacityDeductionCell, BandwidthGroupBreakdownRow } from './bandwidthDashboardTypes';
import {
  exportBandwidthGroupDaywiseDetailExcel,
  exportBandwidthGroupSectionExcel,
} from './bandwidthDashboardExcelExport';
import { exportBandwidthGroupSectionPdf } from './bandwidthDashboardPdfExport';
import {
  bwKpiPanelSubtitleClass,
  bwKpiTableBodyFirstColClassWithBg,
  bwKpiTableFooterRowClass,
  bwKpiTableHeaderCellClass,
  bwKpiTableHeaderClass,
  bwKpiTableHeaderFirstColClass,
  bwKpiTableSelectedRowClass,
  bwKpiTableZebraEven,
  bwKpiTableZebraOdd,
} from './bandwidthKpiTableChrome';
import { fmtBwAvg, fmtBwCount, fmtBwDecimal, fmtBwPercent, roundBwDecimal } from './bandwidthFormat';
import {
  buildCapacityDeductionLookups,
  BW_TEAMS_DEDUCTION_ROW_CLASS,
  getCapacityDeductionHoursForGroupUser,
  getEffectiveBandwidthCapacityHours,
  getGroupEffectiveCapacityHours,
} from './bandwidthCapacityDeductionTypes';
import {
  getBandwidthCapacityHoursForWorkLocation,
  getBandwidthUtilizationPct,
} from './bandwidthUtilization';

export interface BandwidthGroupUserTableProps {
  title: string;
  subtitle?: string;
  groups: BandwidthGroupBreakdownRow[];
  capacityDeductionByGroupUser?: BandwidthCapacityDeductionCell[];
  crossFilter: BandwidthCrossFilterSlice;
  onSelectSlice: (next: BandwidthCrossFilterSlice) => void;
  /** Page-level Assigned To — highlights user rows; clicks sync Active Filters. */
  assigneeUserIds?: string[] | null;
  onAssigneeUserClick?: (userId: string) => void;
  loading?: boolean;
  errorMessage?: string | null;
  exportDateFrom: string;
  exportDateTo: string;
}

function parseYmd(ymdOrDateTime: string): Date | null {
  const ymd = (ymdOrDateTime || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  // Interpret as UTC day boundary to avoid timezone shifting in display.
  const d = new Date(`${ymd}T12:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDateRange(from: string, to: string): string {
  const df = parseYmd(from);
  const dt = parseYmd(to);
  if (!df || !dt) return '—';
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  return `${fmt(df)} - ${fmt(dt)}`;
}

function fmtWorkedDay(ymd: string): string {
  const d = parseYmd(ymd);
  if (!d) return ymd;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

function userDayKey(groupId: string, userId: string): string {
  return `${groupId}:${userId}`;
}

function groupUtilizationSortRatio(
  g: BandwidthGroupBreakdownRow,
  dateFrom: string,
  dateTo: string,
  deductionLookups: ReturnType<typeof buildCapacityDeductionLookups>
): number {
  const cap = getGroupEffectiveCapacityHours(g, dateFrom, dateTo, deductionLookups);
  return cap > 0 ? g.timeSpentHours / cap : -1;
}

type SortKey = 'label' | 'count' | 'hours' | 'avgHrs' | 'utilization';
type SortDir = 'asc' | 'desc';

function sortIndicator(active: boolean, dir: SortDir): string {
  if (!active) return '↕';
  return dir === 'desc' ? '↓' : '↑';
}

function ariaSortForColumn(active: boolean, dir: SortDir): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

const BandwidthGroupUserTable: React.FC<BandwidthGroupUserTableProps> = ({
  title,
  subtitle,
  groups,
  capacityDeductionByGroupUser = [],
  crossFilter,
  onSelectSlice,
  assigneeUserIds = null,
  onAssigneeUserClick,
  loading = false,
  errorMessage = null,
  exportDateFrom,
  exportDateTo,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [expandedUsers, setExpandedUsers] = useState<Record<string, boolean>>({});
  const [sortKey, setSortKey] = useState<SortKey>('hours');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const { run: runDetailExport, modal: detailExportModal } = useClientExportFlow();

  const deductionLookups = useMemo(
    () => buildCapacityDeductionLookups(capacityDeductionByGroupUser),
    [capacityDeductionByGroupUser]
  );

  const sortedGroups = useMemo(() => {
    const dirMul = sortDir === 'desc' ? -1 : 1;
    return [...groups].sort((a, b) => {
      if (sortKey === 'label') {
        return dirMul * a.groupName.localeCompare(b.groupName, undefined, { sensitivity: 'base' });
      }
      if (sortKey === 'count') return dirMul * (a.totalActivityCount - b.totalActivityCount);
      if (sortKey === 'hours') return dirMul * (a.timeSpentHours - b.timeSpentHours);
      if (sortKey === 'utilization') {
        return (
          dirMul *
          (groupUtilizationSortRatio(a, exportDateFrom, exportDateTo, deductionLookups) -
            groupUtilizationSortRatio(b, exportDateFrom, exportDateTo, deductionLookups))
        );
      }
      const avA = a.avgHrsOnActivity ?? -1;
      const avB = b.avgHrsOnActivity ?? -1;
      return dirMul * (avA - avB);
    });
  }, [groups, sortKey, sortDir, exportDateFrom, exportDateTo, deductionLookups]);

  const toggleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'label' ? 'asc' : 'desc');
    }
  }, [sortKey]);

  const sortHeadersDisabled = loading || Boolean(errorMessage) || groups.length === 0;

  const allExpandedKey = useMemo(() => groups.map((g) => g.groupId).sort().join('|'), [groups]);

  React.useEffect(() => {
    setExpanded((prev) => {
      const next: Record<string, boolean> = { ...prev };
      for (const g of groups) {
        if (next[g.groupId] === undefined) next[g.groupId] = false;
      }
      return next;
    });
  }, [allExpandedKey, groups]);

  React.useEffect(() => {
    if (!assigneeUserIds?.length) return;
    setExpanded((prev) => {
      const next = { ...prev };
      for (const g of groups) {
        if (g.users.some((u) => assigneeUserIds.includes(u.userId))) {
          next[g.groupId] = true;
        }
      }
      return next;
    });
  }, [assigneeUserIds, groups]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpanded((p) => ({ ...p, [groupId]: !p[groupId] }));
  }, []);

  const toggleUserDays = useCallback((groupId: string, userId: string) => {
    const key = userDayKey(groupId, userId);
    setExpandedUsers((p) => ({ ...p, [key]: !p[key] }));
  }, []);

  const expandAllDisabled = sortHeadersDisabled || !groups.some((g) => g.users.length > 0);

  const expandableGroupIds = useMemo(
    () => groups.filter((g) => g.users.length > 0).map((g) => g.groupId),
    [groups]
  );

  const allGroupsExpanded = useMemo(() => {
    if (expandableGroupIds.length === 0) return false;
    return expandableGroupIds.every((id) => expanded[id] === true);
  }, [expandableGroupIds, expanded]);

  const expandAllGroups = useCallback(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      for (const g of groups) {
        if (g.users.length > 0) next[g.groupId] = true;
      }
      return next;
    });
  }, [groups]);

  const collapseAllGroups = useCallback(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      for (const g of groups) {
        next[g.groupId] = false;
      }
      return next;
    });
    setExpandedUsers({});
  }, [groups]);

  const toggleExpandAllGroups = useCallback(() => {
    if (allGroupsExpanded) {
      collapseAllGroups();
    } else {
      expandAllGroups();
    }
  }, [allGroupsExpanded, collapseAllGroups, expandAllGroups]);

  const grandTotals = useMemo(() => {
    let count = 0;
    let minutes = 0;
    let totalCapacityHours = 0;
    for (const g of groups) {
      count += g.totalActivityCount;
      minutes += g.timeSpentHours * 60;
      totalCapacityHours += getGroupEffectiveCapacityHours(g, exportDateFrom, exportDateTo, deductionLookups);
    }
    const hrs = minutes / 60;
    const avg = count > 0 ? hrs / count : null;
    const utilPct = getBandwidthUtilizationPct(hrs, totalCapacityHours);
    return {
      count,
      hrs: roundBwDecimal(hrs),
      avg: avg != null ? roundBwDecimal(avg) : null,
      utilPct,
    };
  }, [groups, exportDateFrom, exportDateTo, deductionLookups]);

  const exportDisabled = loading || Boolean(errorMessage) || groups.length === 0;

  const hasUsersForDetailExport = useMemo(
    () => groups.some((g) => g.users.length > 0),
    [groups]
  );

  const detailExportDisabled = exportDisabled || !hasUsersForDetailExport;

  const dateLabel = useMemo(() => fmtDateRange(exportDateFrom, exportDateTo), [exportDateFrom, exportDateTo]);

  const handleExportDetail = useCallback(() => {
    void runDetailExport({
      downloadButtonLabel: 'Download Excel',
      work: async () => {
        const r = exportBandwidthGroupDaywiseDetailExcel(sortedGroups, exportDateFrom, exportDateTo);
        if (!r) throw new Error('No team members to export.');
        return {
          files: r,
          summaryLines: ['Your daywise effort report is ready to download.'],
        };
      },
    });
  }, [runDetailExport, sortedGroups, exportDateFrom, exportDateTo]);

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      titleEnd={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            disabled={detailExportDisabled}
            onClick={handleExportDetail}
            className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-900 shadow-sm transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Export daywise team effort detail"
          >
            Export Detail
          </button>
          <BandwidthPanelExportShell
            disabled={exportDisabled}
            ariaLabel="Export teams breakdown"
            buildPdf={() =>
              exportBandwidthGroupSectionPdf(
                sortedGroups,
                exportDateFrom,
                exportDateTo,
                capacityDeductionByGroupUser
              )
            }
            buildExcel={() =>
              exportBandwidthGroupSectionExcel(
                sortedGroups,
                exportDateFrom,
                exportDateTo,
                capacityDeductionByGroupUser
              )
            }
          />
        </div>
      }
      className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
      flexFill
      bodyClassName="px-0 pb-0 pt-0"
    >
      {detailExportModal}
      {subtitle ? (
        <p className={bwKpiPanelSubtitleClass}>{subtitle}</p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[320px] border-collapse text-left text-xs">
          <thead>
            <tr className={bwKpiTableHeaderClass}>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'label', sortDir)}
                className={`min-w-[140px] px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass} ${bwKpiTableHeaderFirstColClass}`}
              >
                <span className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={expandAllDisabled}
                    onClick={toggleExpandAllGroups}
                    aria-expanded={allGroupsExpanded}
                    aria-label={allGroupsExpanded ? 'Collapse all teams' : 'Expand all teams'}
                    className="-ml-1 inline-flex shrink-0 items-center rounded p-0.5 text-white hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {allGroupsExpanded ? (
                      <ChevronDownIcon className="h-4 w-4" aria-hidden />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" aria-hidden />
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={sortHeadersDisabled}
                    onClick={() => toggleSort('label')}
                    className="inline-flex items-center gap-1 rounded px-0.5 text-left font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                  >
                    Teams
                    <span className="font-normal opacity-90" aria-hidden>
                      {sortIndicator(sortKey === 'label', sortDir)}
                    </span>
                  </button>
                </span>
              </th>
              <th
                scope="col"
                className={`min-w-[150px] px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                Date
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'count', sortDir)}
                className={`min-w-[100px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('count')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Total Activity Count
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'count', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'hours', sortDir)}
                className={`min-w-[100px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('hours')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Total Time Spent (Hrs)
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'hours', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'avgHrs', sortDir)}
                className={`min-w-[90px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('avgHrs')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Avg Hrs On Activity
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'avgHrs', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'utilization', sortDir)}
                className={`min-w-[100px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('utilization')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Utilization %
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'utilization', sortDir)}
                  </span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="text-slate-800">
            {errorMessage ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-red-700">
                  {errorMessage}
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : groups.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                  No approved time entries for this filter.
                </td>
              </tr>
            ) : (
              <>
                {sortedGroups.flatMap((g, gi) => {
                  const isOpen = expanded[g.groupId] === true;
                  const groupSelected = crossFilter?.kind === 'group' && crossFilter.id === g.groupId;
                  const groupRowStripe = gi % 2 === 0 ? bwKpiTableZebraOdd : bwKpiTableZebraEven;
                  const groupCapHours = getGroupEffectiveCapacityHours(
                    g,
                    exportDateFrom,
                    exportDateTo,
                    deductionLookups
                  );
                  const groupCapPerPerson = getBandwidthCapacityHoursForWorkLocation(
                    exportDateFrom,
                    exportDateTo,
                    g.workLocation
                  );
                  const groupUtilPct = getBandwidthUtilizationPct(g.timeSpentHours, groupCapHours);

                  const block: React.ReactNode[] = [
                    <tr
                      key={`g-${g.groupId}`}
                      className={`border-b border-slate-200/90 ${
                        groupSelected
                          ? `${bwKpiTableSelectedRowClass} cursor-pointer`
                          : `${groupRowStripe} cursor-pointer hover:bg-indigo-50/70`
                      }`}
                      onClick={() => {
                        onSelectSlice(
                          crossFilter?.kind === 'group' && crossFilter.id === g.groupId
                            ? null
                            : { kind: 'group', id: g.groupId }
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelectSlice(
                            crossFilter?.kind === 'group' && crossFilter.id === g.groupId
                              ? null
                              : { kind: 'group', id: g.groupId }
                          );
                        }
                      }}
                      tabIndex={0}
                      role="button"
                    >
                      <td
                        className={`min-w-[140px] px-2 py-2 font-semibold text-slate-900 ${bwKpiTableBodyFirstColClassWithBg({ rowIndex: gi, selected: groupSelected })}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            className="rounded p-0.5 text-slate-600 hover:bg-slate-200/80"
                            aria-expanded={isOpen}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleGroup(g.groupId);
                            }}
                          >
                            {isOpen ? (
                              <ChevronDownIcon className="h-4 w-4" aria-hidden />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" aria-hidden />
                            )}
                          </button>
                          <span className="leading-snug">{g.groupName}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-left tabular-nums text-slate-700">{dateLabel}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBwCount(g.totalActivityCount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBwDecimal(g.timeSpentHours)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBwAvg(g.avgHrsOnActivity)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBwPercent(groupUtilPct)}</td>
                    </tr>,
                  ];

                  if (isOpen) {
                    g.users.forEach((u, ui) => {
                      const userSelected =
                        assigneeUserIds != null && assigneeUserIds.includes(u.userId);
                      const stripe =
                        (gi + ui + 1) % 2 === 0 ? bwKpiTableZebraOdd : bwKpiTableZebraEven;
                      const userDeductionHours = getCapacityDeductionHoursForGroupUser(
                        deductionLookups,
                        g.groupId,
                        u.userId
                      );
                      const userCapHours = getEffectiveBandwidthCapacityHours(
                        groupCapPerPerson,
                        userDeductionHours
                      );
                      const userUtilPct = getBandwidthUtilizationPct(u.timeSpentHours, userCapHours);
                      const dayRows = u.days ?? [];
                      const hasDayRows = dayRows.length > 0;
                      const userDaysOpen = expandedUsers[userDayKey(g.groupId, u.userId)] === true;

                      block.push(
                        <tr
                          key={`u-${g.groupId}-${u.userId}`}
                          className={`border-b border-slate-100 ${
                            userSelected
                              ? `${bwKpiTableSelectedRowClass} cursor-pointer`
                              : `${stripe} cursor-pointer hover:bg-indigo-50/70`
                          }`}
                          onClick={() => {
                            if (onAssigneeUserClick) {
                              onAssigneeUserClick(u.userId);
                            } else {
                              onSelectSlice(
                                crossFilter?.kind === 'user' && crossFilter.id === u.userId
                                  ? null
                                  : { kind: 'user', id: u.userId }
                              );
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (onAssigneeUserClick) {
                                onAssigneeUserClick(u.userId);
                              } else {
                                onSelectSlice(
                                  crossFilter?.kind === 'user' && crossFilter.id === u.userId
                                    ? null
                                    : { kind: 'user', id: u.userId }
                                );
                              }
                            }
                          }}
                          tabIndex={0}
                          role="button"
                        >
                          <td
                            className={`min-w-[140px] pl-9 pr-3 py-1.5 font-medium text-slate-700 ${bwKpiTableBodyFirstColClassWithBg({ rowIndex: gi + ui + 1, selected: userSelected })}`}
                          >
                            <span className="inline-flex items-center gap-1">
                              {hasDayRows ? (
                                <button
                                  type="button"
                                  className="rounded p-0.5 text-slate-500 hover:bg-slate-200/80"
                                  aria-expanded={userDaysOpen}
                                  aria-label={`${userDaysOpen ? 'Collapse' : 'Expand'} daywise efforts for ${u.userName}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleUserDays(g.groupId, u.userId);
                                  }}
                                >
                                  {userDaysOpen ? (
                                    <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden />
                                  ) : (
                                    <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden />
                                  )}
                                </button>
                              ) : (
                                <span className="inline-block w-5 shrink-0" aria-hidden />
                              )}
                              <span>{u.userName}</span>
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-left tabular-nums text-slate-600">{dateLabel}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmtBwCount(u.totalActivityCount)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmtBwDecimal(u.timeSpentHours)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmtBwAvg(u.avgHrsOnActivity)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmtBwPercent(userUtilPct)}</td>
                        </tr>
                      );

                      if (hasDayRows && userDaysOpen) {
                        dayRows.forEach((d, di) => {
                          const dayStripe =
                            (gi + ui + di + 2) % 2 === 0 ? bwKpiTableZebraOdd : bwKpiTableZebraEven;
                          const leaveDayClass = d.reducesCapacity ? BW_TEAMS_DEDUCTION_ROW_CLASS : '';
                          block.push(
                            <tr
                              key={`d-${g.groupId}-${u.userId}-${d.workedDate}${d.reducesCapacity ? '-leave' : ''}`}
                              className={`border-b border-slate-100/80 ${dayStripe} text-slate-600`}
                            >
                              <td className={`min-w-[140px] pl-16 pr-3 py-1 ${bwKpiTableBodyFirstColClassWithBg({ rowIndex: gi + ui + di + 2 })}`} />
                              <td className={`px-3 py-1 text-left tabular-nums ${leaveDayClass}`}>
                                {fmtWorkedDay(d.workedDate)}
                              </td>
                              <td className={`px-3 py-1 text-right tabular-nums ${leaveDayClass}`}>
                                {fmtBwCount(d.totalActivityCount)}
                              </td>
                              <td className={`px-3 py-1 text-right tabular-nums ${leaveDayClass}`}>
                                {fmtBwDecimal(d.timeSpentHours)}
                              </td>
                              <td className={`px-3 py-1 text-right tabular-nums ${leaveDayClass}`}>
                                {fmtBwAvg(d.avgHrsOnActivity)}
                              </td>
                              <td className="px-3 py-1 text-right tabular-nums">—</td>
                            </tr>
                          );
                        });
                      }
                    });
                  }

                  return block;
                })}
                <tr className={bwKpiTableFooterRowClass}>
                  <td className={`px-3 py-2.5 ${bwKpiTableBodyFirstColClassWithBg({ footer: true })}`}>Total</td>
                  <td className="px-3 py-2.5 text-left tabular-nums text-slate-600">{dateLabel}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtBwCount(grandTotals.count)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtBwDecimal(grandTotals.hrs)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtBwAvg(grandTotals.avg)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtBwPercent(grandTotals.utilPct)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthGroupUserTable;
